import { Grid } from "@mui/material";
import useTranslation from "../../hooks/useTranslation";
import { JOB_RELATIONSHIP_DEMOGRAPHIC_IDS } from "../../types/report";
import { GetDemographicGroupsResponse } from "../../types/workplaceCulture";
import {
  getChapterIdsFromDemographicsData,
  getOrgInclusionScores,
} from "../../utils/demographics.utils";
import { ScoreCard } from "../ScoreCard/ScoreCard";
import { Chip } from "../ui/Chip/Chip";
import { Paper } from "../ui/Paper/Paper";
import { Typography } from "../ui/Typography/Typography";
import { getSpecialAttentionDemographics } from "./ReportSpecificAttention.utils";
import "./ReportSpecificAttention.scss";

interface ReportSpecificAttentionProps {
  demographicsData?: GetDemographicGroupsResponse;
}

export const ReportSpecificAttention = ({
  demographicsData,
}: ReportSpecificAttentionProps) => {
  const { t } = useTranslation("reportPage");
  const demographicGroups = demographicsData?.demographic_groups || [];
  const chapterIds = getChapterIdsFromDemographicsData(demographicsData);

  return (
    <Paper shadow className="ReportSpecificAttention">
      <Typography
        tagVariant="h2"
        desktop="h2"
        className="ReportSpecificAttention__text"
        color="darkerStoneBlue"
      >
        {t("specificAttention.title")}
      </Typography>

      <Typography
        desktop="body1"
        className="ReportSpecificAttention__text"
        color="secondary"
        fontStyle="italic"
      >
        {t("specificAttention.colorExplanation")}
      </Typography>

      {chapterIds.length > 0 && demographicGroups.length > 0 ? (
        <Grid container spacing={2}>
          {chapterIds.map((chapterId) => {
            const specialAttentionDemographics =
              getSpecialAttentionDemographics(demographicGroups, chapterId);

            // Get chapter title from first demographic's chapter_scores
            const chapterTitle =
              demographicGroups[0]?.demographics[0]?.chapter_scores.find(
                (cs) => cs.id === chapterId
              )?.title || chapterId;

            const { chapterScores } = getOrgInclusionScores(demographicsData);
            const chapterScore = chapterScores[chapterId] || 0;

            return (
              <Grid item xs={12} sm={4} md={4} key={chapterId}>
                <ScoreCard
                  title={chapterTitle}
                  score={chapterScore}
                  label={t("specificAttention.scoreInfo")}
                  scoreSize="medium"
                />
                <div className="ReportSpecificAttention__chapterInfo">
                  {specialAttentionDemographics.length > 0 ? (
                    <div className="ReportSpecificAttention__chapterInfo__chips">
                      {specialAttentionDemographics.map((demographic) => {
                        const chapterScore = demographic.chapter_scores.find(
                          (cs) => cs.id === chapterId
                        );
                        const chipColor =
                          JOB_RELATIONSHIP_DEMOGRAPHIC_IDS.includes(
                            demographic.id as any
                          ) || demographic.id.includes("CDEM.")
                            ? "primary"
                            : "default";
                        return (
                          <Chip
                            key={demographic.id}
                            label={`${t(
                              `scale.groups.${demographic.label}`,
                              demographic.label
                            )} (${chapterScore?.score?.toFixed(2) || "N/A"})`}
                            size="small"
                            color={chipColor}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <Typography
                      desktop="body2"
                      fontStyle="italic"
                      color="secondary"
                    >
                      {t("specificAttention.noGroupsNeedAttention")}
                    </Typography>
                  )}
                </div>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <div className="ReportSpecificAttention__noData">
          <Typography desktop="body1" fontStyle="italic" color="secondary">
            {t("specificAttention.noDataAvailable")}
          </Typography>
        </div>
      )}
    </Paper>
  );
};
